import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { getStaffAttendanceGrid, saveStaffAttendance, processStaffDeviceLog } from "../../api/attendanceApi";
import "./Attendance.css";

const STATUS_LABELS = { 0: "Not taken", 1: "Present", 2: "Absent", 3: "Leave", 4: "Not mapped" };

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function StaffAttendanceTakePage() {
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadGrid = () => {
    setLoading(true);
    setError("");
    getStaffAttendanceGrid(date)
      .then(setRows)
      .catch(() => setError("Failed to load attendance grid."))
      .finally(() => setLoading(false));
  };

  useEffect(loadGrid, [date]);

  const updateRow = (staffId, field, value) => {
    setRows((prev) => prev.map((r) => (r.staffId === staffId ? { ...r, [field]: value } : r)));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const entries = rows.map((r) => ({
        staffId: r.staffId,
        attendanceStatus: r.attendanceStatus || 2,
        inTime: r.inTime,
        outTime: r.outTime,
      }));
      const updated = await saveStaffAttendance({ date, entries });
      setRows(updated);
      setSuccess("Attendance saved.");
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save attendance.");
    } finally {
      setSaving(false);
    }
  };

  const handleProcessDevice = async () => {
    setProcessing(true);
    setError("");
    setSuccess("");
    try {
      const result = await processStaffDeviceLog({ date });
      setSuccess(result.message);
      loadGrid();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to process device log.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Staff Attendance</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="attendance-filters">
        <label>
          Date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <button type="button" className="btn-secondary" onClick={handleProcessDevice} disabled={processing}>
          {processing ? "Processing..." : "Process Device Data"}
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div className="table-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>In</th>
                <th>Out</th>
                <th>In Status</th>
                <th>Out Status</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.staffId}>
                  <td>{row.customStaffId}</td>
                  <td>{row.staffName}</td>
                  <td>{row.inTime || "—"}</td>
                  <td>{row.outTime || "—"}</td>
                  <td>{row.inStatus || ""}</td>
                  <td>{row.outStatus || ""}</td>
                  <td>
                    <select value={row.attendanceStatus} onChange={(e) => updateRow(row.staffId, "attendanceStatus", Number(e.target.value))}>
                      {Object.entries(STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Attendance"}
            </button>
          </div>
        </>
      )}
    </MainLayout>
  );
}
